import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "./header.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faXmark,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import cx from "classnames";

import ImageLogo from "../../assets/images/logos/logo.png";
import ImageHeader1 from "../../assets/images/template/header-1.jpg";
import ImageHeader2 from "../../assets/images/template/header-2.jpg";
import ImageHeader3 from "../../assets/images/template/header-3.jpg";
import ImageHeader4 from "../../assets/images/template/header-4.jpg";
import ImageHeader5 from "../../assets/images/template/header-5.jpg";
import ImageHeader6 from "../../assets/images/template/header-6.jpg";
import ImageHeader7 from "../../assets/images/template/header-7.jpg";
import ImageHeader8 from "../../assets/images/template/header-8.jpg";
import ImageHeader9 from "../../assets/images/template/header-9.jpg";
import ImageHeader10 from "../../assets/images/template/header-10.jpg";

import menuData from "./menu";
import { BUY_TICKETS_URL, shouldShowTicketsButton } from "../../utils/eventHelpers";

// Helper to check if any submenu matches the current path
function isMenuItemActive(item, currentPath) {
  if (item.path === currentPath) return true;
  if (item.subMenu) {
    return item.subMenu.some((sub) => isMenuItemActive(sub, currentPath));
  }
  return false;
}

// Helper to check if a menu item is an ancestor of the active item
function isAncestorActive(item, currentPath) {
  if (item.subMenu) {
    return item.subMenu.some((sub) => {
      if (sub.path === currentPath) return true;
      return isAncestorActive(sub, currentPath);
    });
  }
  return false;
}

// Helper: returns true if this item or any descendant matches currentPath
function isMenuItemOrDescendantActive(item, currentPath) {
  if (item.path === currentPath) return true;
  if (item.subMenu) {
    return item.subMenu.some((sub) =>
      isMenuItemOrDescendantActive(sub, currentPath)
    );
  }
  return false;
}

// Desktop menu rendering with dropdown control
function renderMenu(
  items,
  level = 1,
  currentPath = "",
  openDropdown,
  setOpenDropdown,
  parentKey = ""
) {
  if (!items || !items.length) return null;
  return (
    <ul className={`menu-list level-${level}`}>
      {items.map((item, idx) => {
        const hasSubMenu = !!item.subMenu;
        // Build a hierarchical key for each menu item
        const key = parentKey ? `${parentKey}-${idx}` : `${level}-${idx}`;
        const submenuId = hasSubMenu ? `submenu-${key}` : undefined;
        const isActive = isMenuItemActive(item, currentPath);
        const isAncestor = isAncestorActive(item, currentPath);
        const highlightClass = isActive || isAncestor ? "active-red" : "";
        const isDropdownOpen = openDropdown && openDropdown.startsWith(key);

        return (
          <li
            key={item.path + (item.id ?? "")}
            className={cx({ "has-children": hasSubMenu, open: isDropdownOpen })}
            onMouseEnter={() => hasSubMenu && setOpenDropdown(key)}
            onMouseLeave={() => hasSubMenu && setOpenDropdown(parentKey)}
          >
            <NavLink
              to={item.path}
              className={({ isActive: navIsActive }) =>
                cx("menu-link", { [highlightClass]: highlightClass })
              }
              aria-haspopup={hasSubMenu ? "true" : undefined}
              aria-expanded={hasSubMenu ? isDropdownOpen : undefined}
              aria-controls={submenuId}
              onClick={!hasSubMenu ? () => setOpenDropdown(null) : undefined}
            >
              {item.icon && (
                <FontAwesomeIcon icon={item.icon} style={{ marginRight: 8 }} />
              )}
              {item.name}
              {hasSubMenu && (
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="submenu-arrow"
                />
              )}
            </NavLink>
            {hasSubMenu &&
              isDropdownOpen &&
              renderMenu(
                item.subMenu,
                level + 1,
                currentPath,
                openDropdown,
                setOpenDropdown,
                key
              )}
          </li>
        );
      })}
    </ul>
  );
}

// Helper to build a unique key for each menu item at each level
function getExpandKey(parentPath, idx) {
  return `${parentPath}|${idx}`;
}

// Recursive menu rendering for mobile (expand/collapse)
function renderMobileMenu(
  items,
  expanded,
  toggleExpand,
  closeMenu,
  level = 1,
  currentPath = "",
  parentPath = ""
) {
  if (!items || !items.length) return null;
  return (
    <ul className={`mobile-menu-list level-${level}`}>
      {items.map((item, idx) => {
        const hasSubMenu = !!item.subMenu;
        const expandKey = getExpandKey(parentPath, idx);
        const isExpanded = expanded[expandKey];
        const isActiveOrAncestor = isMenuItemOrDescendantActive(
          item,
          currentPath
        );

        return (
          <li
            key={item.path + (item.id ?? "")}
            className={cx({ "has-children": hasSubMenu, expanded: isExpanded })}
          >
            <div
              className={cx("mobile-menu-link", {
                "active-red": isActiveOrAncestor,
              })}
            >
              {hasSubMenu ? (
                <button
                  className="mobile-menu-label"
                  onClick={() => toggleExpand(parentPath, idx)}
                  aria-label={
                    isExpanded ? "Collapse submenu" : "Expand submenu"
                  }
                  tabIndex={0}
                  type="button"
                >
                  {item.icon && (
                    <FontAwesomeIcon
                      icon={item.icon}
                      style={{ marginRight: 8 }}
                    />
                  )}
                  {item.name}
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className={
                      isExpanded ? "submenu-arrow rotated" : "submenu-arrow"
                    }
                  />
                </button>
              ) : (
                <NavLink
                  to={item.path}
                  onClick={closeMenu}
                  tabIndex={0}
                  className={cx({ "active-red": isActiveOrAncestor })}
                >
                  {item.icon && (
                    <FontAwesomeIcon
                      icon={item.icon}
                      style={{ marginRight: 8 }}
                    />
                  )}
                  {item.name}
                </NavLink>
              )}
            </div>
            {hasSubMenu &&
              isExpanded &&
              renderMobileMenu(
                item.subMenu,
                expanded,
                toggleExpand,
                closeMenu,
                level + 1,
                currentPath,
                expandKey
              )}
          </li>
        );
      })}
    </ul>
  );
}

function Header({ currentPage }) {
  const [menuItems] = useState(menuData);
  const parallaxRef = useRef(null);
  const trackRef = useRef(null);
  const location = useLocation();

  // Array of header images for slideshow (with duplicate first image for seamless loop)
  const headerImages = [
    ImageHeader1,
    ImageHeader2,
    ImageHeader3,
    ImageHeader4,
    ImageHeader5,
    ImageHeader6,
    ImageHeader7,
    ImageHeader8,
    ImageHeader9,
    ImageHeader10,
  ];

  const headerImagesWithDuplicate = [...headerImages, headerImages[0]]; // Add first image at end for seamless loop

  // State for current image index and slideshow - start on random image if animating, or specific image if h param provided
  const [currentImageIndex, setCurrentImageIndex] = useState(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const animateParam = queryParams.get("animate");
    const headerImageParam = queryParams.get("h");
    
    if (animateParam) {
      if (headerImageParam) {
        const imageNum = parseInt(headerImageParam);
        if (imageNum >= 1 && imageNum <= 10) {
          return imageNum - 1; // Start animation from specified image
        }
      }
      return Math.floor(Math.random() * headerImages.length); // Random start if no valid h param
    }
    return 0; // Default to first image if no animation
  });

  // Parse query string for header image selection
  const queryParams = new URLSearchParams(location.search);
  const headerImageParam = queryParams.get("h");
  const animateParam = queryParams.get("animate");
  let selectedImageIndex = currentImageIndex; // default to slideshow

  // If h parameter is present but no animate parameter, show static image
  if (headerImageParam && !animateParam) {
    const imageNum = parseInt(headerImageParam);
    if (imageNum >= 1 && imageNum <= 10) {
      selectedImageIndex = imageNum - 1;
    }
  }

  // Track which dropdown is open (by key)
  const [openDropdown, setOpenDropdown] = useState(null);

  // Mobile menu state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (parentPath, idx) => {
    setExpanded((prev) => {
      const expandKey = `${parentPath}|${idx}`;
      // Collapse all siblings at this parentPath, expand only the clicked one
      const newExpanded = Object.keys(prev)
        .filter((key) => !key.startsWith(parentPath + "|") || key === expandKey)
        .reduce((acc, key) => ({ ...acc, [key]: prev[key] }), {});
      return {
        ...newExpanded,
        [expandKey]: !prev[expandKey],
      };
    });
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setExpanded({});
  };

  // Continuous slideshow effect with seamless looping - cycle through images every 5 seconds (only if animate param is present)
  useEffect(() => {
    if (!animateParam) return; // Only animate if animate param exists

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        
        // If we've reached the duplicate image (index 10), prepare for seamless reset
        if (nextIndex === headerImages.length) {
          // Use setTimeout to reset to beginning after the transition completes
          setTimeout(() => {
            if (trackRef.current) {
              // Temporarily disable transition for instant reset
              trackRef.current.style.transition = 'none';
              setCurrentImageIndex(0);
              
              // Re-enable transition after a brief moment
              setTimeout(() => {
                if (trackRef.current) {
                  trackRef.current.style.transition = 'transform 0.8s ease-in-out';
                }
              }, 50);
            }
          }, 800); // Wait for current transition to complete
        }
        
        return nextIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [animateParam, headerImages.length]);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      if (parallaxRef.current) {
        parallaxRef.current.style.backgroundPosition = `center calc(50% + ${scrolled * 0.5}px)`;
      }
    };

    // Set initial position to center
    if (parallaxRef.current) {
      parallaxRef.current.style.backgroundPosition = "center center";
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    closeMobileMenu();
    // eslint-disable-next-line
  }, [location.pathname]);

  return (
    <>
      {/* Fixed Menu Bar */}
      <nav className="fixed-menu-bar">
        <div className="menu-logo large-logo">
          <NavLink to={menuItems[0]?.path || "/"}>
            <img src={ImageLogo} alt="Logo" />
          </NavLink>
        </div>
        <div className="menu-container desktop-menu">
          {renderMenu(
            menuItems,
            1,
            location.pathname,
            openDropdown,
            setOpenDropdown
          )}
          {shouldShowTicketsButton() && (
          <a
            href={BUY_TICKETS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="buy-tickets-btn"
          >
            Buy Tickets
          </a>
          )}
        </div>
        <button
          className="hamburger"
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
      </nav>

      {/* Mobile Menu Panel */}
      <div className={cx("mobile-menu-panel", { open: mobileOpen })}>
        <button
          className="mobile-menu-close"
          aria-label="Close menu"
          onClick={closeMobileMenu}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        {renderMobileMenu(
          menuItems,
          expanded,
          toggleExpand,
          closeMobileMenu,
          1,
          location.pathname
        )}
        {shouldShowTicketsButton() && (
        <a
          href={BUY_TICKETS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="buy-tickets-btn mobile"
        >
          Buy Tickets
        </a>
        )}
      </div>

      {/* Parallax Header */}
      <div className="parallax-header-container">
        <div className="parallax-header-slideshow">
          <div 
            ref={trackRef}
            className="parallax-header-track"
            style={{
              transform: animateParam
                ? `translateX(-${currentImageIndex * 100}vw)`
                : headerImageParam
                  ? `translateX(-${selectedImageIndex * 100}vw)`
                  : `translateX(0)` // No animation, show first image
            }}
          >
            {(animateParam ? headerImagesWithDuplicate : headerImages).map((image, index) => (
              <div
                key={index}
                className="parallax-header-image"
                ref={index === (animateParam ? currentImageIndex : (headerImageParam ? selectedImageIndex : 0)) ? parallaxRef : null}
                style={{
                  backgroundImage: `url(${image})`,
                  backgroundPositionY: "center center",
                }}
              />
            ))}
          </div>
        </div>
        
        <div className="parallax-header-title">
          <h1>Always the last Saturday in September</h1>
        </div>
      </div>
    </>
  );
}

export default Header;
